import { useCallback, useEffect, useState } from "react"
import axios from "axios"
import { API_URL } from "../../../../constants"
import CardMarkerSmall from "../../../../components/CockpitCards/CardMarkerSmall"
import InvoicesText from "./InvoicesText"
import ButtonsMain from "../../../../components/Button/ButtonMain"
import {
  Stack,
  Typography,
  Chip,
  Skeleton,
  Alert,
  IconButton,
  CircularProgress,
  Tooltip,
  Divider,
} from "@mui/material"
import VisibilityIcon from "@mui/icons-material/Visibility"
import DownloadIcon from "@mui/icons-material/Download"
import { useTranslation } from "../../../../language"

const STATUS_COLOR = {
  paid: "success",
  open: "warning",
  void: "default",
  uncollectible: "error",
  draft: "default",
}

const STATUS_KEY = {
  paid: "invoices_status_paid",
  open: "invoices_status_open",
  void: "invoices_status_void",
  uncollectible: "invoices_status_uncollectible",
  draft: "invoices_status_draft",
}

const formatAmount = (cents, currency) =>
  new Intl.NumberFormat(undefined, {
    style: "currency",
    currency: (currency || "eur").toUpperCase(),
  }).format((cents || 0) / 100)

export default function InvoicesCard({ sx }) {

  const { t } = useTranslation()

  const [invoices, setInvoices] = useState([])
  const [loading, setLoading] = useState(true)
  const [loadingMore, setLoadingMore] = useState(false)
  const [error, setError] = useState(false)
  const [loadMoreError, setLoadMoreError] = useState(false)
  const [hasMore, setHasMore] = useState(false)

  const fetchInvoices = useCallback(async (startingAfter) => {
    const params = { limit: 20 }
    if (startingAfter) params.starting_after = startingAfter
    const res = await axios.get(`${API_URL}/api/account/invoices/`, { params })
    setInvoices((prev) => (startingAfter ? [...prev, ...res.data.invoices] : res.data.invoices))
    setHasMore(res.data.has_more)
  }, [])

  useEffect(() => {
    (async () => {
      setLoading(true)
      setError(false)
      try {
        await fetchInvoices()
      } catch {
        setError(true)
      } finally {
        setLoading(false)
      }
    })()
  }, [fetchInvoices])

  const handleLoadMore = async () => {
    if (invoices.length === 0) return
    setLoadingMore(true)
    setLoadMoreError(false)
    try {
      await fetchInvoices(invoices[invoices.length - 1].id)
    } catch {
      setLoadMoreError(true)
    } finally {
      setLoadingMore(false)
    }
  }

  if (loading) return <Skeleton height={240} />

  return (
    <CardMarkerSmall
      sx={sx}
      headline={t("invoices_headline")}
      subtext={<InvoicesText />}
      cardlines={
        <Stack spacing={2} sx={{ mt: 1 }}>
          {error && <Alert severity="error">{t("invoices_fetch_error")}</Alert>}

          {!error && invoices.length === 0 && (
            <Typography color="text.secondary">{t("invoices_empty")}</Typography>
          )}

          {!error && invoices.length > 0 && (
            <Stack divider={<Divider />}>
              {invoices.map((invoice) => (
                <InvoiceRow key={invoice.id} invoice={invoice} t={t} />
              ))}
            </Stack>
          )}

          {loadMoreError && <Alert severity="error">{t("invoices_fetch_error")}</Alert>}

          {hasMore && !error && (
            <ButtonsMain
              action={handleLoadMore}
              disabled={loadingMore}
              name={loadingMore ? <CircularProgress size={18} color="inherit" /> : t("invoices_load_more")}
            />
          )}
        </Stack>
      }
    />
  )
}

function InvoiceRow({ invoice, t }) {
  const amount = invoice.status === "paid" ? invoice.amount_paid : invoice.amount_due

  return (
    <Stack
      direction={{ xs: "column", sm: "row" }}
      spacing={1.5}
      alignItems={{ xs: "flex-start", sm: "center" }}
      justifyContent="space-between"
      sx={{ py: 1.5 }}
    >
      <Stack spacing={0.25} sx={{ minWidth: 0 }}>
        <Typography fontWeight={600}>{invoice.number || invoice.id}</Typography>
        <Typography variant="caption" color="text.secondary">
          {new Date(invoice.created).toLocaleDateString()}
          {" • "}
          {t(invoice.is_subscription ? "invoices_type_subscription" : "invoices_type_addon")}
        </Typography>
      </Stack>

      <Stack direction="row" spacing={1} alignItems="center">
        <Chip
          size="small"
          label={t(STATUS_KEY[invoice.status] || invoice.status)}
          color={STATUS_COLOR[invoice.status] || "default"}
        />
        <Typography sx={{ minWidth: 72, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>
          {formatAmount(amount, invoice.currency)}
        </Typography>

        <Tooltip title={t("invoices_view")}>
          <span>
            <IconButton
              size="small"
              component="a"
              href={invoice.hosted_invoice_url || undefined}
              target="_blank"
              rel="noopener noreferrer"
              disabled={!invoice.hosted_invoice_url}
            >
              <VisibilityIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>

        <Tooltip title={t("invoices_download")}>
          <span>
            <IconButton
              size="small"
              component="a"
              href={invoice.invoice_pdf || undefined}
              target="_blank"
              rel="noopener noreferrer"
              disabled={!invoice.invoice_pdf}
            >
              <DownloadIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </Stack>
    </Stack>
  )
}
